export type InlineMarkdownMarks = {
  bold: boolean;
  italic: boolean;
  strike: boolean;
};

export type InlineMarkdownSegment = {
  text: string;
  marks: InlineMarkdownMarks;
};

const EMPTY_MARKS: InlineMarkdownMarks = {
  bold: false,
  italic: false,
  strike: false,
};

const MARKER_PATTERN = /(\*\*|__|~~|\*|_)/g;

const isOpenMarker = (
  marker: string,
  active: InlineMarkdownMarks,
): boolean => {
  if (marker === '**' || marker === '__') {
    return !active.bold;
  }
  if (marker === '~~') {
    return !active.strike;
  }
  return !active.italic;
};

const toggleMarkForMarker = (
  marker: string,
  active: InlineMarkdownMarks,
): InlineMarkdownMarks => {
  if (marker === '**' || marker === '__') {
    return { ...active, bold: !active.bold };
  }
  if (marker === '~~') {
    return { ...active, strike: !active.strike };
  }
  return { ...active, italic: !active.italic };
};

export const hasInlineMarkdownMarkers = (value: string): boolean => {
  if (!value) {
    return false;
  }
  MARKER_PATTERN.lastIndex = 0;
  return MARKER_PATTERN.test(value);
};

export const parseInlineMarkdown = (value: string): InlineMarkdownSegment[] => {
  if (!value) {
    return [];
  }

  const segments: InlineMarkdownSegment[] = [];
  let active: InlineMarkdownMarks = { ...EMPTY_MARKS };
  let cursor = 0;
  let buffer = '';

  const flush = (): void => {
    if (!buffer) {
      return;
    }
    segments.push({ text: buffer, marks: { ...active } });
    buffer = '';
  };

  MARKER_PATTERN.lastIndex = 0;
  let match = MARKER_PATTERN.exec(value);
  while (match) {
    const marker = match[1]!;
    const index = match.index;
    buffer += value.slice(cursor, index);

    const nextChar = value[index + marker.length];
    const prevChar = index > 0 ? value[index - 1] : '';
    const looksLikeEmphasis =
      isOpenMarker(marker, active)
        ? nextChar !== undefined && nextChar !== ' ' && nextChar !== '\n'
        : prevChar !== '' && prevChar !== ' ' && prevChar !== '\n';

    if (looksLikeEmphasis) {
      flush();
      active = toggleMarkForMarker(marker, active);
    } else {
      buffer += marker;
    }

    cursor = index + marker.length;
    match = MARKER_PATTERN.exec(value);
  }

  buffer += value.slice(cursor);
  flush();
  return segments;
};

export const stripInlineMarkdown = (value: string): string =>
  parseInlineMarkdown(value).map((segment) => segment.text).join('');
